import React, { useState } from 'react';
import { Button } from '../components/Button';
import { Mail, Phone, MapPin, Send, Calendar, Clock, Laptop, Smartphone } from 'lucide-react';

export const Contact: React.FC = () => {
  const [subject, setSubject] = useState('General Inquiry');

  return (
    <div className="pt-20">
       <div className="bg-secondary py-20 px-6 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-primary/5"></div>
          <div className="relative z-10">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">Get in Touch</h1>
            <p className="text-gray-400 text-lg">We're here to help. Reach out to us anytime.</p>
          </div>
       </div>

       <div className="container mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-2 gap-16">
              {/* Contact Info */}
              <div className="space-y-12">
                  <div>
                      <h2 className="text-3xl font-bold text-secondary mb-6">Contact Information</h2>
                      <p className="text-gray-600 leading-relaxed">
                          Whether you have questions about the app, need help with a booking, or just want to give feedback, our support team is ready to assist you.
                      </p>
                  </div>

                  <div className="space-y-8">
                      <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-primary flex-shrink-0">
                              <MapPin size={24} />
                          </div>
                          <div>
                              <h4 className="font-bold text-secondary text-lg">Headquarters</h4>
                              <p className="text-gray-500">281a Herbert Chitepo Avenue<br/>Harare</p>
                          </div>
                      </div>

                      <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-primary flex-shrink-0">
                              <Phone size={24} />
                          </div>
                          <div>
                              <h4 className="font-bold text-secondary text-lg">Support</h4>
                              <p className="text-gray-500">Call: +263 776 621 188</p>
                              <p className="text-gray-500">WhatsApp: +263 776 621 119</p>
                              <p className="text-gray-400 text-sm mt-1">Mon-Fri 8am-8pm</p>
                          </div>
                      </div>

                      <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-primary flex-shrink-0">
                              <Mail size={24} />
                          </div>
                          <div>
                              <h4 className="font-bold text-secondary text-lg">Email</h4>
                              <p className="text-gray-500">support@menshealthclinic.africa</p>
                              <p className="text-gray-400 text-sm mt-1">24/7 Response time</p>
                          </div>
                      </div>
                  </div>
              </div>

              {/* Dynamic Form */}
              <div className="bg-white p-8 md:p-10 rounded-3xl shadow-xl shadow-gray-100 border border-gray-100">
                  <h3 className="text-2xl font-bold text-secondary mb-6">Send us a Message</h3>
                  <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                      <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                              <label className="text-sm font-bold text-gray-700">First Name</label>
                              <input type="text" className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder="John" />
                          </div>
                          <div className="space-y-2">
                              <label className="text-sm font-bold text-gray-700">Last Name</label>
                              <input type="text" className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder="Doe" />
                          </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                              <label className="text-sm font-bold text-gray-700">Email Address</label>
                              <input type="email" className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder="john@example.com" />
                          </div>
                          <div className="space-y-2">
                              <label className="text-sm font-bold text-gray-700">Phone Number</label>
                              <input type="tel" className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder="+263 770 000 000" />
                          </div>
                      </div>

                       <div className="space-y-2">
                          <label className="text-sm font-bold text-gray-700">Subject</label>
                          <select 
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                          >
                              <option>General Inquiry</option>
                              <option>Make Appointment</option>
                              <option>Technical Support</option>
                              <option>Partnership</option>
                              <option>Other</option>
                          </select>
                      </div>

                      {/* Appointment Fields */}
                      {subject === 'Make Appointment' && (
                        <div className="grid md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                    <Calendar size={14} className="text-primary" /> Preferred Date
                                </label>
                                <input type="date" className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                    <Clock size={14} className="text-primary" /> Preferred Time
                                </label>
                                <select className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all">
                                    <option>Morning (8am - 12pm)</option>
                                    <option>Afternoon (12pm - 4pm)</option>
                                    <option>Evening (4pm - 8pm)</option>
                                </select>
                            </div>
                        </div>
                      )}

                      {/* Technical Support Fields */}
                      {subject === 'Technical Support' && (
                        <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                            <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                <Laptop size={14} className="text-primary" /> Platform / Device
                            </label>
                            <select className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all">
                                <option>iOS App</option>
                                <option>Android App</option>
                                <option>Web Dashboard</option>
                                <option>Other</option>
                            </select>
                        </div>
                      )}

                      <div className="space-y-2">
                          <label className="text-sm font-bold text-gray-700">Message</label>
                          <textarea rows={4} className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none" placeholder={subject === 'Make Appointment' ? "Tell us briefly about the reason for your visit..." : "How can we help you?"}></textarea>
                      </div>

                      <Button type="submit" className="w-full py-4 gap-2">
                          {subject === 'Make Appointment' ? 'Request Appointment' : 'Send Message'} <Send size={18} />
                      </Button>
                  </form>
              </div>
          </div>
       </div>
    </div>
  )
}